import { useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

const AUTH_INTENT_KEY = 'bquest_auth_intent';

export type SSOButtonProps = {
  companySlug: string;
  connectionId?: string | null;
  orgCode?: string | null;
  email?: string;
  onError?: (error: string | null) => void;
  error?: string | null;
  disabled?: boolean;
  className?: string;
};

const SSOButton: React.FC<SSOButtonProps> = ({
  companySlug,
  connectionId,
  orgCode,
  email,
  onError,
  error,
  disabled = false,
  className = '',
}) => {
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();
  const [debouncing, setDebouncing] = useState(false);

  const handleSSOLogin = async () => {
    if (debouncing || kindeLoading) {
      return;
    }

    setDebouncing(true);

    try {
      onError?.(null);
      sessionStorage.setItem(
        AUTH_INTENT_KEY,
        JSON.stringify({
          method: 'sso',
          companySlug,
          email,
          expectedCompanySlug: companySlug,
        }),
      );

      await kindeLogin({
        ...(connectionId ? { connectionId } : {}),
        ...(orgCode ? { orgCode } : {}),
        ...(email ? { loginHint: email } : {}),
      });
    } catch (error) {
      console.error('SSO authentication error:', error);
      onError?.('SSO authentication failed. Please try again.');
    } finally {
      setTimeout(() => setDebouncing(false), 1000);
    }
  };

  const isDisabled = disabled || kindeLoading || debouncing;
  const buttonText = kindeLoading ? 'Connecting...' : 'Sign in with SSO';

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleSSOLogin}
        disabled={isDisabled}
        aria-label={kindeLoading ? 'Connecting to SSO' : 'Sign in with SSO'}
        aria-disabled={isDisabled}
        className={`font-termina font-black rounded-3xl py-3 px-6 w-max uppercase border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${className}`}
      >
        {buttonText}
      </button>
      {error && (
        <p className="text-red-600 text-sm mt-2 font-termina" role="alert" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
};

export default SSOButton;
